import {
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  Injector,
  Input,
} from '@angular/core';
import { CRMIntegrationsService } from '../../../services/crm-integrations.service';
import { tap, filter, concatMap } from 'rxjs/operators';
import { publicMethods } from '../../../globals';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ZintModalConfig, ZintModalContent } from '../../../classes/types';

@Component({
    selector: 'wizard-crm-permissions',
    templateUrl: './wizard-crm-permissions.component.html',
    styleUrl: './wizard-crm-permissions.component.css',
    standalone: false
})
export class WizardCrmPermissionsComponent {
  @Input() stepData: Record<string, any>;

  crmService = inject(CRMIntegrationsService);
  pubMethods = inject(publicMethods);
  injector = inject(Injector);
  snackbar = inject(MatSnackBar);
  cdr = inject(ChangeDetectorRef);

  CRM_OBJECT_TYPE = 'crm_object_type';
  EXPORT_PERMISSIONS = 'export_permissions';

  crmObjectID = computed(() => {
    return this.crmService.configCRMSelectedObjectId();
  });
  exporterPermissionsId: number = null;
  existingPermissions: Record<string, any> = {};
  updateObjectOwnerFlag = false;

  // raw api data
  teamsAndUsers: Record<string, any> = {};

  allUsers: Record<string, any>[] = [];
  allTeams: Record<string, any>[] = [];

  crmType: string = '';
  stepType: string = '';

  showOwnerHelpModal = false;
  ownerHelpModalConfig: ZintModalConfig;
  ownerHelpModalContent: ZintModalContent;

  ngOnInit(): void {
    this.crmType = this.crmService.configCRMType();
    this.stepType = this.stepData.stepType;
    this.getStepActionContent();
  }

  getStepActionContent(): void {
    const objectIdAndExporterPermsId$ = this.crmService.getCRMExistingState({
      actionType: this.CRM_OBJECT_TYPE,
    });

    this.crmService
      .getCRMExistingState({ actionType: this.CRM_OBJECT_TYPE })
      .subscribe(data => {
        if (this.pubMethods.hasData(data)) {
          if (!this.crmObjectID()) {
            this.crmService.configCRMSelectedObjectId.update(() => data[0].id);
          }
          this.updateObjectOwnerFlag = data[0].shouldUpdateOwnerToCurrentUser;
        }
      });

    objectIdAndExporterPermsId$
      .pipe(
        tap(data => {
          // setup required IDs
          if (this.pubMethods.hasData(data)) {
            this.crmService.configCRMSelectedObjectId.update(() => data[0].id);

            this.exporterPermissionsId = data[0].exporterPermissionsId;
          }
        }),
        concatMap(() => {
          // get existing state
          return this.crmService.getCRMExistingState({
            actionType: this.EXPORT_PERMISSIONS,
            objectId: this.exporterPermissionsId,
          });
        }),
        tap(existingData => {
          this.existingPermissions.organisations =
            existingData['viewableByOrganisations'];
          this.existingPermissions.users = existingData['viewableByUsers'];
        }),
        concatMap(() => {
          // get all available permissions data
          return this.crmService.getCRMStepActionContent({
            stepType: this.stepType,
            actionType: this.EXPORT_PERMISSIONS,
          });
        }),
        filter(allPermsData => this.pubMethods.hasData(allPermsData)),
        tap(allPermsData => {
          this.teamsAndUsers = allPermsData;

          this.allUsers = allPermsData.organisationUsers;

          this.allTeams = allPermsData.organisationTeams;
        })
      )
      .subscribe();
  }

  handlePermissionsEvent(event: any) {
    const { userId, id: teamId, selected } = event;
    let userOrOrg: 'user' | 'org';
    if ('userId' in event && 'email' in event) {
      userOrOrg = 'user';
    }
    if ('id' in event && 'name' in event) {
      userOrOrg = 'org';
    }

    this.updateViewableUserOrOrg(userId || teamId, userOrOrg, selected);
  }

  updateViewableUserOrOrg(
    userOrOrgId: number,
    userOrOrg: 'user' | 'org',
    isAdding: boolean
  ): void {
    const permissionUpdateAction = isAdding
      ? `add_${userOrOrg}_to_viewable`
      : `remove_${userOrOrg}_from_viewable`;

    const paramKey = userOrOrg === 'user' ? 'userId' : 'orgId';
    const params = { [paramKey]: userOrOrgId };

    this.crmService
      .postCRMStepActionJSON({
        postActionType: this.EXPORT_PERMISSIONS,
        objectId: this.exporterPermissionsId,
        params,
        permissionsUpdateType: permissionUpdateAction,
      })
      .subscribe();
  }

  handleToggleUpdateObjectOwnerFlag(): void {
    const reqBody = {
      shouldUpdateOwnerToCurrentUser: this.updateObjectOwnerFlag,
    };

    this.crmService
      .updateCRMStepActionContent({
        updateActionType: this.CRM_OBJECT_TYPE,
        objectId: this.crmObjectID(),
        updateMethod: 'patch',
        params: reqBody,
      })
      .pipe(
        tap(response => {
          if (response.ok) {
            const settingString = this.updateObjectOwnerFlag
              ? 'enabled'
              : 'disabled';
            if (!this.updateObjectOwnerFlag) {
              this.snackbar.open('Setting ' + settingString, 'OK', {
                duration: 2200,
              });
            }
          }
        })
      )
      .subscribe();
  }

  toggleOwnerHelp(): void {
    this.ownerHelpModalConfig = { name: 'ownerHelp', vCentered: true };
    this.ownerHelpModalContent = {
      plainText: `When activated, if a user you have granted access to via these permissions attempts to create a new object in your CRM via the integration, this will automatically update the owner of that new object to the user who initiated the creation request. \n
      For some CRMs (e.g. Salesforce) this is the default behaviour and therefore makes no difference but for others (e.g. HubSpot) this needs to be explicitly activated.`,
    };

    this.showOwnerHelpModal = true;
    this.cdr.detectChanges();
    this.pubMethods.triggerZintModal(this.ownerHelpModalConfig.name);
  }
}
